import type { DraftRequest } from '../pipeline/types';

const API_ORIGIN = 'https://api.anthropic.com';
const MESSAGES_PATH = '/v1/messages';
const API_VERSION = '2023-06-01';

export interface AnthropicSystemBlock {
  type: 'text';
  text: string;
  cache_control?: { type: 'ephemeral' };
}

export type AnthropicContentBlock =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; id: string; name: string; input: unknown }
  | { type: 'tool_result'; tool_use_id: string; content: string; is_error?: boolean };

export interface AnthropicMessage {
  role: 'user' | 'assistant';
  content: string | AnthropicContentBlock[];
}

export interface AnthropicToolDefinition {
  name: string;
  description: string;
  input_schema: Record<string, unknown>;
}

export interface AnthropicRequestBody {
  model: string;
  max_tokens: number;
  system: AnthropicSystemBlock[];
  messages: AnthropicMessage[];
  stream: boolean;
  tools?: AnthropicToolDefinition[];
}

export interface UsageInfo {
  input_tokens?: number;
  output_tokens?: number;
  cache_creation_input_tokens?: number;
  cache_read_input_tokens?: number;
}

function labeledBlockText(label: string, content: string): string {
  return `# ${label}\n${content}`;
}

/**
 * Builds the system blocks for a draft. If the draft has a CachePlan (i.e. D2 ran), the stable
 * prefix gets a `cache_control` breakpoint, and the summary gets its own if `summaryIsCacheable`.
 * Without a CachePlan (D2 disabled), this falls back to one flat, uncached system block — the
 * observable difference toggling D2 off is meant to produce.
 */
export function buildSystemBlocks(draft: DraftRequest): AnthropicSystemBlock[] {
  const system: AnthropicSystemBlock[] = [{ type: 'text', text: draft.systemPrompt }];

  if (draft.cachePlan) {
    const stableIds = new Set(draft.cachePlan.stableContextBlockIds);
    const stableBlocks = draft.contextBlocks.filter((b) => stableIds.has(b.id));
    const otherBlocks = draft.contextBlocks.filter((b) => !stableIds.has(b.id));

    if (stableBlocks.length) {
      const text = stableBlocks.map((b) => labeledBlockText(b.label, b.content)).join('\n\n');
      system.push({ type: 'text', text, cache_control: { type: 'ephemeral' } });
    }
    for (const b of otherBlocks) {
      system.push({ type: 'text', text: labeledBlockText(b.label, b.content) });
    }
    if (draft.historySummary) {
      system.push({
        type: 'text',
        text: `Conversation summary so far:\n${draft.historySummary}`,
        ...(draft.cachePlan.summaryIsCacheable ? { cache_control: { type: 'ephemeral' } as const } : {}),
      });
    }
  } else {
    const blockTexts = draft.contextBlocks.map((b) => labeledBlockText(b.label, b.content));
    if (draft.historySummary) blockTexts.push(`Conversation summary so far:\n${draft.historySummary}`);
    if (blockTexts.length) system.push({ type: 'text', text: blockTexts.join('\n\n') });
  }

  return system;
}

/** Builds the wire-format request for a normal (non-agentic) streaming send. */
export function buildRequestBody(draft: DraftRequest, model: string, maxTokens: number): AnthropicRequestBody {
  const system = buildSystemBlocks(draft);
  const messages: AnthropicMessage[] = draft.history.map((t) => ({ role: t.role, content: t.content }));
  messages.push({ role: 'user', content: draft.userMessage });

  return { model, max_tokens: maxTokens, system, messages, stream: true };
}

/**
 * Builds the initial request for an agentic tool-use loop: same context assembly as a normal
 * send, plus an extra (uncached — small, persona-specific) system block and the tool definitions
 * the caller's persona allows. Non-streaming: tool-use round trips don't benefit from token-by-
 * token streaming, and only the final answer needs to render live.
 */
export function buildAgentRequestBody(
  draft: DraftRequest,
  model: string,
  maxTokens: number,
  tools: AnthropicToolDefinition[],
  personaPrompt: string,
): AnthropicRequestBody {
  const system = buildSystemBlocks(draft);
  if (personaPrompt) system.push({ type: 'text', text: personaPrompt });
  const messages: AnthropicMessage[] = draft.history.map((t) => ({ role: t.role, content: t.content }));
  messages.push({ role: 'user', content: draft.userMessage });

  return { model, max_tokens: maxTokens, system, messages, stream: false, tools };
}

export class AnthropicApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

function anthropicHeaders(apiKey: string): Record<string, string> {
  return {
    'content-type': 'application/json',
    'x-api-key': apiKey,
    'anthropic-version': API_VERSION,
    'anthropic-dangerous-direct-browser-access': 'true',
  };
}

export interface MessageResponse {
  content: AnthropicContentBlock[];
  stopReason: string | undefined;
  usage: UsageInfo;
}

/** Non-streaming send, used by the agentic tool-use loop — each round trip needs the complete
 * response (including any tool_use blocks) before deciding whether to loop again. */
export async function sendMessageOnce(apiKey: string, body: AnthropicRequestBody, signal?: AbortSignal): Promise<MessageResponse> {
  if (!apiKey) throw new AnthropicApiError(401, 'No Anthropic API key set.');

  const res = await fetch(API_ORIGIN + MESSAGES_PATH, {
    method: 'POST',
    headers: anthropicHeaders(apiKey),
    body: JSON.stringify({ ...body, stream: false }),
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new AnthropicApiError(res.status, text || res.statusText);
  }

  const json = (await res.json()) as { content: AnthropicContentBlock[]; stop_reason?: string; usage: UsageInfo };
  return { content: json.content, stopReason: json.stop_reason, usage: json.usage };
}

export interface StreamEvent {
  textDelta?: string;
  usage?: UsageInfo;
  stopReason?: string;
  done?: boolean;
}

/**
 * Streams a Messages API response directly from the browser. The key never
 * leaves this device except in the `x-api-key` header of this request to
 * api.anthropic.com.
 */
export async function* streamMessage(
  apiKey: string,
  body: AnthropicRequestBody,
  signal?: AbortSignal,
): AsyncGenerator<StreamEvent> {
  if (!apiKey) throw new AnthropicApiError(401, 'No Anthropic API key set.');

  const res = await fetch(API_ORIGIN + MESSAGES_PATH, {
    method: 'POST',
    headers: anthropicHeaders(apiKey),
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => res.statusText);
    throw new AnthropicApiError(res.status, text || res.statusText);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let usage: UsageInfo = {};

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const events = buffer.split('\n\n');
    buffer = events.pop() ?? '';

    for (const chunk of events) {
      const dataLine = chunk.split('\n').find((l) => l.startsWith('data:'));
      if (!dataLine) continue;
      const payload = JSON.parse(dataLine.slice(5).trim());

      if (payload.type === 'message_start' && payload.message?.usage) {
        usage = { ...usage, ...payload.message.usage };
        yield { usage };
      } else if (payload.type === 'content_block_delta' && payload.delta?.type === 'text_delta') {
        yield { textDelta: payload.delta.text as string };
      } else if (payload.type === 'message_delta') {
        if (payload.usage) usage = { ...usage, ...payload.usage };
        yield { usage, stopReason: payload.delta?.stop_reason };
      } else if (payload.type === 'message_stop') {
        yield { done: true };
      }
    }
  }
}
